import { GENERAL, axiosAiq } from 'config';
import { ACTIVITY_EVENTS_ENDPOINT } from 'utils/activityTracking/constants';

/**
 * The only module that talks to `POST /activity/events` (BEAST-13036).
 *
 * The browser NEVER talks to the shared Activity Event Service. Architecture rule, verbatim:
 * "Product browsers report interaction intent only to their authenticated backend." AIQ's backend
 * stamps the trusted identity and forwards the batch onward.
 *
 * Pure transport: it posts what it is handed and reports what came back. Batching, capping,
 * splitting and retry all live in the queue - this module has no policy of its own beyond never
 * throwing at its caller, because no tracking failure may reach the agent.
 */

/** The request body is `{ events: [...] }` and nothing else - the backend generates its own batch id. */
function toRequestBody(events) {
    return { events };
}

/**
 * `Retry-After` is honoured when present. It comes in two shapes per RFC 9110 - delay seconds or an
 * HTTP date - and it rides on the edge-generated statuses (429 / 503), so it is a header we read
 * even though we never read those bodies.
 */
function parseRetryAfterMs(headers) {
    const raw = headers?.['retry-after'] ?? headers?.['Retry-After'];
    if (raw === undefined || raw === null || raw === '') return null;

    const seconds = Number(raw);
    if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);

    const asDate = Date.parse(raw);
    if (Number.isFinite(asDate)) return Math.max(0, asDate - Date.now());

    return null;
}

/**
 * @returns {Promise<{ ok: boolean, status: number|null, rejected: Array, networkError: boolean,
 *   retryAfterMs: number|null }>} Never rejects. `status` is `null` only for a network-level failure
 *   with no response at all.
 */
export async function postActivityEvents(events) {
    try {
        const response = await axiosAiq.post(ACTIVITY_EVENTS_ENDPOINT, toRequestBody(events));
        const status = response?.status ?? null;

        // Parse a response body ONLY on a 202. Every other status either has no body of ours or has
        // an edge-generated one (a 413 can be an HTML page from Tomcat, and 429/502/504 come from
        // nginx / ALB / WAF), so reading them would be parsing someone else's error page.
        if (status === 202) {
            const rejected = Array.isArray(response?.data?.rejected) ? response.data.rejected : [];
            return { ok: true, status, rejected, networkError: false, retryAfterMs: null };
        }

        return { ok: false, status, rejected: [], networkError: false, retryAfterMs: parseRetryAfterMs(response?.headers) };
    } catch (error) {
        const status = error?.response?.status ?? null;
        return {
            ok: false,
            status,
            rejected: [],
            networkError: status === null,
            retryAfterMs: parseRetryAfterMs(error?.response?.headers),
        };
    }
}

/**
 * Trailing flush for the paths where a normal XHR may not survive: document hide, page unload,
 * logout and desk leave.
 *
 * TWO THINGS FAIL SILENTLY HERE.
 *
 * The Blob type must be set EXPLICITLY. The endpoint declares `consumes = application/json`, which
 * accepts `application/json` and `application/json;charset=utf-8` and nothing else - and a
 * default-typed Blob sends `text/plain`, which WILL be rejected.
 *
 * And `sendBeacon` is the one transport in this feature that fails by returning a boolean rather
 * than by throwing or answering with a status. Browsers cap the total size of in-flight beacons
 * (64 KB in Chromium) and over it it returns false and sends nothing, so the return value has to be
 * checked. There is no retry available on an unloading page and a synchronous XHR is not an
 * acceptable substitute, so a false return is logged and the events are dropped.
 *
 * The beacon path cannot read a response and does not have to: the Keycloak filter never issues a
 * 302 to a login page, so there is no invisible "redirected to HTML login" failure mode. It is also
 * same-origin here (the SPA and the API are served by the same Tomcat), so there is no preflight.
 *
 * @returns {boolean} whether the browser accepted the beacon for sending.
 */
export function beaconActivityEvents(events) {
    try {
        if (!events?.length) return true;
        if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false;

        const body = new Blob([JSON.stringify(toRequestBody(events))], { type: 'application/json' });
        const sent = navigator.sendBeacon(`${GENERAL.ADDRESSES.HTTP.MAIN}${ACTIVITY_EVENTS_ENDPOINT}`, body);

        if (!sent) {
            console.warn(`activityTracking: sendBeacon refused ${events.length} event(s); they are dropped.`);
        }
        return sent;
    } catch (error) {
        console.warn('activityTracking: sendBeacon threw; events are dropped.', error);
        return false;
    }
}
